import translator from 'src/translate'
import { withStyles } from '@material-ui/core/styles'
import { Link as RouterLink } from 'react-router-dom'
import React, { useState, useEffect, useRef } from 'react'
import { track } from '@amplitude/analytics-browser'
import styles from '../styles'
import {
  Box,
  CircularProgress,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  MenuItem,
  InputAdornment,
  Divider,
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { useCourseSettings } from 'src/hooks/useCourseSettings'
import { Icon } from '@iconify/react'
import CustomizedSwitch from 'src/components/customized-switch'
import { Badge } from 'src/components/user-access/components'
import UpgradePopover from 'src/components/upgrade-popover/upgrade-popover'
import {
  settingsDrawerOpenSuccess,
  coursePatchSuccess,
} from 'src/reducers/course/action'
import { updateCourseCodeService } from 'src/api/course'
import { AssignmentFieldNames } from 'src/model/assignment'
import { splitCourseCode, normalizeCourseSlug } from 'src/utils/course-code'
import { CoreButton, CoreTextField, CoreIconButton } from 'src/components/core'
import UserType from 'src/model/user-types'
import {
  kokoroAmericanEnglishVoices,
  kokoroBritishEnglishVoices,
  kokoroJapaneseVoices,
  kokoroMandarinChineseVoices,
  kokoroSpanishVoices,
  kokoroFrenchVoices,
  kokoroItalianVoices,
  kokoroBrazilianPortugueseVoices,
  kokoroHindiVoices,
  languages,
} from './data/ttsVoices'

const SETTING_KEY = 'general'

const AdvancedSettings = props => {
  // `onSaved` replaces the drawer close when the tab is rendered inline.
  const { classes, onSaved } = props
  const dispatch = useDispatch()
  const { execute, update, exists, value } = useCourseSettings(SETTING_KEY)
  const course = useSelector(state => state.course.course)
  const { enqueueSnackbar } = useSnackbar()

  const [loading, setLoading] = useState(false)

  // The drawer unmounts its content on close, so unsaved edits are dropped and reopening starts
  // from the last saved code.
  const courseCode = splitCourseCode(course?.[AssignmentFieldNames.CODE])
  const [slug, setSlug] = useState(courseCode ? courseCode.slug : '')
  const savedSlug = normalizeCourseSlug(slug)
  // A code already saved without a slug (e.g. "-61564577") must not block saving other settings.
  const isSlugEmpty =
    Boolean(courseCode) && savedSlug === '' && courseCode.slug !== ''

  useEffect(() => {
    setSlug(courseCode ? courseCode.slug : '')
  }, [course?.[AssignmentFieldNames.CODE]])

  const isUserPremium = Number(window.CONSTANTS.USER_ROLE_ID) !== 1

  const userControl = UserType[window.CONSTANTS.USER_ROLE_ID]
  const hasScriptAccess =
    userControl !== 'Basic' && !UserType.isTrialEquivalent(userControl)
  const hasAccessibilityAccess = () => {
    if (
      userControl == 'Elite' ||
      userControl == 'AppSumo (Tier 5)' ||
      userControl == 'AppSumo (Tier 6)' ||
      userControl == 'AppSumo (Tier 7)' ||
      userControl == 'AppSumo (Tier 8)' ||
      userControl == 'AppSumo (Tier 9)' ||
      userControl == 'Super Admin' ||
      userControl == 'Plus' ||
      userControl == 'Pro v2' ||
      userControl == 'Launch' ||
      userControl == 'Essentials' ||
      userControl == 'Growth' ||
      userControl == 'Scale' ||
      userControl == 'Enterprise' ||
      UserType.isTrialEquivalent(userControl)
    ) {
      return true
    }
    return false
  }

  const getVoicesForLanguage = languageCode => {
    switch (languageCode) {
      case 'en-US':
        return kokoroAmericanEnglishVoices
      case 'en-GB':
        return kokoroBritishEnglishVoices
      case 'ja':
        return kokoroJapaneseVoices
      case 'zh':
        return kokoroMandarinChineseVoices
      case 'es':
        return kokoroSpanishVoices
      case 'fr':
        return kokoroFrenchVoices
      case 'hi':
        return kokoroHindiVoices
      case 'it':
        return kokoroItalianVoices
      case 'pt':
        return kokoroBrazilianPortugueseVoices
      default:
        return []
    }
  }

  const [thumbnailUrl, setThumbnailUrl] = useState(exists ? value.logo : '')

  const [headerScript, setHeaderScript] = useState(
    exists ? value.headerScript : '',
  )

  const [bodyScript, setBodyScript] = useState(exists ? value.bodyScript : '')
  const [sendEmailOnFinish, setSendEmailOnFinish] = useState(
    exists && value.sendEmailOnFinish != undefined
      ? value.sendEmailOnFinish
      : false,
  )
  const [isSequential, setIsSequential] = useState(
    exists && (value.isSequential != undefined ? value.isSequential : false),
  )

  const [enableAccessibilityWidget, setEnableAccessibilityWidget] = useState(
    exists && value.enableAccessibilityWidget != undefined
      ? value.enableAccessibilityWidget
      : false,
  )

  const [enablePageReader, setEnablePageReader] = useState(
    exists && value.enablePageReader != undefined
      ? value.enablePageReader
      : false,
  )

  const getInitialPageReaderSettings = () => {
    if (
      exists &&
      value.pageReaderSettings &&
      value.pageReaderSettings.textToSpeech
    ) {
      const tts = value.pageReaderSettings.textToSpeech
      const language = tts.language || languages[0].code

      let initialVoice = tts.voice || ''
      if (!initialVoice) {
        const voices = getVoicesForLanguage(language)
        initialVoice = voices.length > 0 ? voices[0].value : ''
      }

      return {
        textToSpeech: {
          value: typeof tts.value === 'boolean' ? tts.value : true,
          mode: tts.mode || (tts.value === 'advanced' ? 'advanced' : 'basic'),
          language: language,
          voice: initialVoice,
        },
      }
    }

    const defaultLanguage = languages[0].code
    const defaultVoices = getVoicesForLanguage(defaultLanguage)

    return {
      textToSpeech: {
        value: false,
        mode: 'basic',
        language: defaultLanguage,
        voice: defaultVoices.length > 0 ? defaultVoices[0].value : '',
      },
    }
  }

  const [pageReaderSettings, setPageReaderSettings] = useState(
    getInitialPageReaderSettings(),
  )

  const [headerScriptIsExist, setHeaderScriptIsExist] = useState(
    (headerScript != '' && headerScript != null && headerScript != undefined) ||
      (bodyScript != '' && bodyScript != null && bodyScript != undefined),
  )
  const [anchorEl, setAnchorEl] = useState(null)
  const [popupType, setPopupType] = useState('pro')
  const open = Boolean(anchorEl)

  const getPopupContent = () => {
    if (popupType === 'pro') {
      return {
        header: 'Upgrade to Plus',
        content: 'Upgrade to Plus plan to access this feature.',
      }
    } else {
      return {
        header: 'Upgrade to Plus',
        content: 'Upgrade to Plus plan to access accessibility features.',
      }
    }
  }

  const popupContent = getPopupContent()

  const saveCourseCode = async () => {
    // Compare normalized forms: older slugs can be longer than 45 characters and must not be
    // shortened just because Save was pressed.
    if (!courseCode || savedSlug === normalizeCourseSlug(courseCode.slug))
      return true

    try {
      const response = await updateCourseCodeService(
        course[AssignmentFieldNames.ID],
        savedSlug,
      )
      // Codes without a suffix get a new one from the backend, so only it knows the final code.
      const code =
        response?.data?.code ??
        response?.code ??
        (courseCode.suffix && `${savedSlug}-${courseCode.suffix}`)
      if (code) {
        dispatch(
          coursePatchSuccess({
            _contents: [{ path: AssignmentFieldNames.CODE, value: code }],
          }),
        )
      }
      enqueueSnackbar('Course URL updated.', {
        variant: 'success',
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
      })
      return true
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.operationMessage ||
          'Could not update the course URL.',
        { variant: 'error' },
      )
      return false
    }
  }

  const saveSettings = async () => {
    if (isSlugEmpty) return

    setLoading(true)
    const codeSaved = await saveCourseCode()
    setLoading(false)
    if (!codeSaved) return

    const tts = pageReaderSettings.textToSpeech

    const textToSpeech = {
      value: !!tts.value,
      mode: tts.mode,
      language: tts.language,
      voice: tts.voice,
    }

    const variables = {
      ...value,
      isSequential: isSequential,
      logo: thumbnailUrl,
      headerScript: headerScript,
      bodyScript: bodyScript,
      sendEmailOnFinish: sendEmailOnFinish,
      enableAccessibilityWidget: enableAccessibilityWidget,
      enablePageReader: !!tts.value,
      pageReaderSettings: { textToSpeech },
    }

    if (!exists) {
      execute(variables, () => {
        setLoading(false)
        handleDrawerClose()
      })
    } else {
      update(variables, () => {
        setLoading(false)
        handleDrawerClose()
      })
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleIsSequential = () => {
    if (!isUserPremium) {
      setPopupType('pro')
      setAnchorEl(document.getElementById('isSequential'))
    } else {
      setIsSequential(!isSequential)
    }
  }

  const handleSetSendEmailOnFinish = () => {
    if (!isUserPremium) {
      setPopupType('pro')
      setAnchorEl(document.getElementById('sendEmailOnFinish'))
    } else {
      setSendEmailOnFinish(!sendEmailOnFinish)
    }
  }

  const handleEnableAccessibilityWidget = () => {
    if (!hasAccessibilityAccess()) {
      setPopupType('elite')
      setAnchorEl(document.getElementById('enableAccessibilityWidget'))
    } else {
      setEnableAccessibilityWidget(!enableAccessibilityWidget)
    }
  }

  const handleEnablePageReader = () => {
    if (!hasAccessibilityAccess()) {
      setPopupType('elite')
      setAnchorEl(document.getElementById('enablePageReader'))
    } else {
      setPageReaderSettings(prev => ({
        ...prev,
        textToSpeech: {
          ...prev.textToSpeech,
          value: !prev.textToSpeech.value,
        },
      }))
    }
  }

  const handleHeaderScriptIsExist = () => {
    setHeaderScriptIsExist(!headerScriptIsExist)
  }

  const handleScriptUpgradeRedirect = () => {
    if (hasScriptAccess) {
      return
    }

    track('limit_warning_upgrade', {
      from: 'course_script_limit_upgrade',
    })
    window.location.href = '/pricing'
  }

  const id = open ? 'simple-popover' : undefined

  const handleDrawerClose = () => {
    if (onSaved) onSaved()
    else dispatch(settingsDrawerOpenSuccess())
  }

  const [playingVoice, setPlayingVoice] = useState(null)
  const voiceAudioRef = useRef(null)

  const handlePlayVoice = voiceValue => {
    const voices = getCurrentVoices()
    const selected = voices.find(v => v.value === voiceValue)
    const url =
      selected && selected.audioUrl
        ? selected.audioUrl
        : 'https://cdn.minicoursegenerator.com/assets/tts/basic/basic-sample.mp3'

    if (!voiceAudioRef.current) {
      voiceAudioRef.current = new window.Audio(url)
      voiceAudioRef.current.playbackRate = 1.0
      voiceAudioRef.current.onended = () => setPlayingVoice(null)
    }

    if (playingVoice === voiceValue) {
      voiceAudioRef.current.pause()
      voiceAudioRef.current.currentTime = 0
      setPlayingVoice(null)
    } else {
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause()
        voiceAudioRef.current = null
      }
      voiceAudioRef.current = new window.Audio(url)
      voiceAudioRef.current.playbackRate = 1.0
      voiceAudioRef.current.onended = () => setPlayingVoice(null)
      voiceAudioRef.current.play()
      setPlayingVoice(voiceValue)
    }
  }

  useEffect(() => {
    return () => {
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause()
        voiceAudioRef.current = null
      }
    }
  }, [])

  const [playingLanguage, setPlayingLanguage] = useState(null)

  const handlePlayLanguage = languageValue => {
    if (playingLanguage === languageValue) {
      setPlayingLanguage(null)
      return
    }
    setPlayingLanguage(languageValue)
  }

  useEffect(() => {
    const intercomWidget = document.querySelector('.intercom-lightweight-app')
    if (intercomWidget) {
      intercomWidget.style.display = 'none'
    }
    return () => {
      const intercomWidget = document.querySelector('.intercom-lightweight-app')
      if (intercomWidget) {
        intercomWidget.style.display = 'block'
      }
    }
  }, [])

  const voiceOptions = []

  useEffect(() => {
    const voices = getCurrentVoices()

    if (
      pageReaderSettings.textToSpeech.mode === 'advanced' &&
      voices.length > 0 &&
      (!pageReaderSettings.textToSpeech.voice ||
        !voices.some(v => v.value === pageReaderSettings.textToSpeech.voice))
    ) {
      setPageReaderSettings(prev => ({
        ...prev,
        textToSpeech: {
          ...prev.textToSpeech,
          voice: voices[0].value,
        },
      }))
    }
  }, [
    pageReaderSettings.textToSpeech.language,
    pageReaderSettings.textToSpeech.mode,
  ])

  const getCurrentVoices = () => {
    return getVoicesForLanguage(pageReaderSettings.textToSpeech.language)
  }

  const voices = getCurrentVoices()
  const selectedVoice =
    pageReaderSettings.textToSpeech.voice &&
    voices.some(v => v.value === pageReaderSettings.textToSpeech.voice)
      ? pageReaderSettings.textToSpeech.voice
      : (voices[0] && voices[0].value) || ''

  const [isBasicPlaying, setIsBasicPlaying] = useState(false)
  const basicAudioRef = useRef(null)

  const handlePlayBasic = () => {
    if (!basicAudioRef.current) {
      basicAudioRef.current = new window.Audio(
        'https://cdn.minicoursegenerator.com/assets/tts/basic/basic-sample.mp3',
      )
      basicAudioRef.current.playbackRate = 1.0
      basicAudioRef.current.onended = () => setIsBasicPlaying(false)
    }

    if (isBasicPlaying) {
      basicAudioRef.current.pause()
      basicAudioRef.current.currentTime = 0
      setIsBasicPlaying(false)
    } else {
      basicAudioRef.current.playbackRate = 1.0
      basicAudioRef.current.play()
      setIsBasicPlaying(true)
    }
  }

  useEffect(() => {
    return () => {
      if (basicAudioRef.current) {
        basicAudioRef.current.pause()
        basicAudioRef.current = null
      }
    }
  }, [])

  return (
    <>
      <div className={classes.content}>
        <Box m={1}></Box>

        {courseCode && (
          <>
            <CoreTextField
              fullWidth
              id="courseUrl"
              label="Course URL"
              variant="outlined"
              value={slug}
              onChange={event =>
                setSlug(
                  normalizeCourseSlug(event.target.value, {
                    keepTrailingHyphen: true,
                  }),
                )
              }
              onBlur={() => setSlug(value => value.replace(/-+$/, ''))}
              error={isSlugEmpty}
              helperText={
                isSlugEmpty
                  ? 'Enter at least one letter or number.'
                  : 'Only the part before the number can change. Use lowercase letters, numbers and hyphens. Your old link will stop working after you save.'
              }
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: courseCode.suffix && (
                  <InputAdornment position="end">
                    <Typography style={{ color: '#919EAB' }}>
                      -{courseCode.suffix}
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
            <Box m={1.5}></Box>
            <Divider />
            <Box m={1.5}></Box>
          </>
        )}

        <div
          className={classes.advancedSettings}
          onClick={() => handleIsSequential()}
        >
          <div className={classes.advancedSettings_content}>
            <Typography>Learners can proceed only sequentially</Typography>
            <Badge type={4} />
          </div>

          <CustomizedSwitch id="isSequential" checked={isSequential} />
        </div>

        <Box m={0.5}></Box>
        <div
          style={{
            width: '100%',
            height: isSequential ? '100%' : '0px',
            opacity: isSequential ? 1 : 0,
            transition: 'all 0.5s',
            overflow: 'hidden',
          }}
        >
          <Typography
            style={{
              color: '#637381',
            }}
            fontSize={'14px'}
            fontWeight={400}
          >
            Learners can't proceed unless previous cards were viewed.
          </Typography>
        </div>
        <Box m={1}></Box>
        <div
          className={classes.advancedSettings}
          onClick={() => handleSetSendEmailOnFinish()}
        >
          <div className={classes.advancedSettings_content}>
            <Typography>Notify by email when someone completes</Typography>
            <Badge type={4} />
          </div>

          <CustomizedSwitch
            id="sendEmailOnFinish"
            checked={sendEmailOnFinish}
          />
        </div>
        <Box m={0.5}></Box>
        <div
          style={{
            width: '100%',
            height: sendEmailOnFinish ? '100%' : '0px',
            opacity: sendEmailOnFinish ? 1 : 0,
            transition: 'all 0.5s',
            overflow: 'hidden',
          }}
        >
          <Typography
            style={{
              color: '#637381',
            }}
            fontSize={'14px'}
            fontWeight={400}
          >
            Recommended if you need to contact the lead promptly after mini
            course is completed.
          </Typography>
        </div>
        <Box m={1}></Box>
        <div
          className={classes.advancedSettings}
          onClick={() => handleHeaderScriptIsExist()}
        >
          <div className={classes.advancedSettings_content}>
            <Typography>Add header and body scripts</Typography>
            {!hasScriptAccess && (
              <div
                style={{
                  display: 'flex',
                  height: '20px',
                  borderRadius: '5px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: '8px',
                  marginTop: '3px',
                  backgroundColor: '#015ffb14',
                  padding: '0 8px',
                  marginBottom: '4px',
                }}
              >
                <Typography
                  fontSize={'11px'}
                  fontWeight={500}
                  style={{ color: '#015FFB' }}
                >
                  Plus
                </Typography>
              </div>
            )}
          </div>

          <CustomizedSwitch
            id="headerScriptIsExist"
            checked={headerScriptIsExist}
          />
        </div>
        <Box m={1}></Box>
        <div
          style={{
            width: '100%',
            height: headerScriptIsExist ? '100%' : '0px',
            opacity: headerScriptIsExist ? 1 : 0,
            transition: 'all 0.5s',
            overflow: 'hidden',
          }}
        >
          <Typography
            style={{
              color: '#00B8D9',
            }}
            fontSize={'14px'}
            fontWeight={400}
          >
            Header Script
          </Typography>
          <Box m={1}></Box>
          <div onClick={() => !hasScriptAccess && handleScriptUpgradeRedirect()}>
            <CoreTextField
              multiline
              rows="5"
              fullWidth
              placeholder={translator._('assignment.courseSettings.headerText')}
              id="headerScript"
              label={translator._('assignment.courseSettings.headerScript')}
              variant="outlined"
              defaultValue={headerScript}
              onBlur={event => setHeaderScript(event.target.value)}
              disabled={!hasScriptAccess || !headerScriptIsExist}
            />
          </div>
          <Box m={1}></Box>
          <Typography
            style={{
              color: '#FFAB00',
            }}
            fontSize={'14px'}
            fontWeight={400}
          >
            Body Script
          </Typography>
          <Box m={1}></Box>
          <div onClick={() => !hasScriptAccess && handleScriptUpgradeRedirect()}>
            <CoreTextField
              multiline
              rows="5"
              fullWidth
              placeholder={translator._('assignment.courseSettings.bodyText')}
              id="headerScript"
              label={translator._('assignment.courseSettings.bodyScript')}
              variant="outlined"
              defaultValue={bodyScript}
              onBlur={event => setBodyScript(event.target.value)}
              disabled={!hasScriptAccess || !headerScriptIsExist}
            />
          </div>
        </div>
        <Box m={1}></Box>
        {course && course.courseCollectionId === null && (
          <>
            <div
              className={classes.advancedSettings}
              onClick={() => handleEnableAccessibilityWidget()}
            >
              <div className={classes.advancedSettings_content}>
                <Typography>Show Accessibility Widget</Typography>
                <Badge type={6} />
              </div>

              <CustomizedSwitch
                id="enableAccessibilityWidget"
                checked={enableAccessibilityWidget}
              />
            </div>
            <Box m={0.5}></Box>
            <div
              style={{
                width: '100%',
                transition: 'all 0.5s',
                overflow: 'hidden',
              }}
            >
              <Typography
                style={{
                  color: '#637381',
                }}
                fontSize={'14px'}
                fontWeight={400}
              >
                Show a floating accessibility menu to assist learners with
                disabilities.
              </Typography>
            </div>
            <Box m={1}></Box>
          </>
        )}
        <div className={classes.advancedSettings}>
          <div className={classes.advancedSettings_content}>
            <Typography>Page Reader (Text to Speech)</Typography>
          </div>
          <CustomizedSwitch
            id="enablePageReader"
            checked={pageReaderSettings.textToSpeech.value}
            onChange={() =>
              setPageReaderSettings(prev => ({
                ...prev,
                textToSpeech: {
                  ...prev.textToSpeech,
                  value: !prev.textToSpeech.value,
                },
              }))
            }
            color="primary"
            name="enablePageReader"
            inputProps={{ 'aria-label': 'Enable Page Reader' }}
          />
        </div>
        <Box m={0.5}></Box>
        <div
          style={{
            width: '100%',
            height: pageReaderSettings.textToSpeech.value ? '100%' : '0px',
            opacity: pageReaderSettings.textToSpeech.value ? 1 : 0,
            transition: 'all 0.5s',
            overflow: 'hidden',
          }}
        >
          {pageReaderSettings.textToSpeech.value && (
            <Box mt={2}>
              <Typography fontSize={'14px'} fontWeight={500} mb={1}>
                Choose your TTS Type
              </Typography>
              <RadioGroup
                value={pageReaderSettings.textToSpeech.mode}
                onChange={e => {
                  const mode = e.target.value
                  if (mode === 'advanced' && !hasAccessibilityAccess()) {
                    setPopupType('elite')
                    setAnchorEl(document.getElementById('advancedTTSRadio'))
                    return
                  }

                  const currentLanguage =
                    pageReaderSettings.textToSpeech.language ||
                    languages[0].code
                  const newVoices = getVoicesForLanguage(currentLanguage)

                  setPageReaderSettings(prev => ({
                    ...prev,
                    textToSpeech: {
                      ...prev.textToSpeech,
                      mode,
                      ...(mode === 'advanced'
                        ? {
                            language: currentLanguage,
                            voice:
                              newVoices.length > 0 ? newVoices[0].value : '',
                          }
                        : {
                            language: languages[0].code,
                            voice: '',
                          }),
                    },
                  }))
                }}
              >
                <div className={classes.readerBox}>
                  <FormControlLabel
                    value="basic"
                    control={<Radio color="primary" />}
                    label={
                      <Typography className={classes.readerText}>
                        Basic Reader
                      </Typography>
                    }
                    style={{ margin: 0 }}
                  />
                  <Typography className={classes.readerTextInformation}>
                    Read aloud using your browser's default voice.
                  </Typography>
                  <CoreButton
                    className={classes.playButton}
                    color="secondary"
                    size="small"
                    variant="outlined"
                    aria-label="Play basic reader demo"
                    onClick={handlePlayBasic}
                  >
                    <Icon
                      icon={isBasicPlaying ? 'ion:pause' : 'iconoir:play-solid'}
                      className={classes.playIcon}
                    />{' '}
                    <Typography>{isBasicPlaying ? 'Pause' : 'Play'}</Typography>
                  </CoreButton>
                </div>
                <div className={classes.readerBox}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <FormControlLabel
                      value="advanced"
                      control={<Radio color="primary" id="advancedTTSRadio" />}
                      label={
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          <Typography className={classes.readerText}>
                            Advanced Voice Reader
                          </Typography>
                          <Badge type={6} />
                        </span>
                      }
                      style={{ margin: 0 }}
                    />
                  </div>
                  <Typography className={classes.readerTextInformation}>
                    Choose from a range of high-quality voices and languages.
                  </Typography>
                  {hasAccessibilityAccess() &&
                    pageReaderSettings.textToSpeech.mode === 'advanced' && (
                      <Box width="100%" className={classes.selectBox}>
                        <CoreTextField
                          select
                          fullWidth
                          label="Language"
                          value={pageReaderSettings.textToSpeech.language}
                          onChange={e => {
                            const newLanguage = e.target.value
                            const newVoices = getVoicesForLanguage(newLanguage)
                            setPageReaderSettings(prev => ({
                              ...prev,
                              textToSpeech: {
                                ...prev.textToSpeech,
                                language: newLanguage,
                                voice:
                                  newVoices.length > 0
                                    ? newVoices[0].value
                                    : '',
                              },
                            }))
                          }}
                          SelectProps={{
                            renderValue: selected => {
                              const found = languages.find(
                                l => l.code === selected,
                              )
                              return found ? found.name : selected || ''
                            },
                            MenuProps: {
                              getContentAnchorEl: null,
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                            },
                          }}
                        >
                          {languages.map(l => (
                            <MenuItem
                              key={l.code + l.name}
                              value={l.code}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <span>{l.name}</span>
                            </MenuItem>
                          ))}
                        </CoreTextField>
                        <CoreTextField
                          select
                          fullWidth
                          label="Voice"
                          value={selectedVoice}
                          onChange={e =>
                            setPageReaderSettings(prev => ({
                              ...prev,
                              textToSpeech: {
                                ...prev.textToSpeech,
                                voice: e.target.value,
                              },
                            }))
                          }
                          margin="dense"
                          variant="outlined"
                          SelectProps={{
                            renderValue: selected => {
                              const found = voices.find(
                                v => v.value === selected,
                              )
                              return found ? found.label : selected || ''
                            },
                            MenuProps: {
                              getContentAnchorEl: null,
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                            },
                          }}
                        >
                          {voices.map(v => (
                            <MenuItem
                              key={v.value}
                              value={v.value}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                {v.gender === 'female' && (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#f783ac"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{ marginRight: 4 }}
                                  >
                                    <path d="M12 9m-5 0a5 5 0 1 0 10 0a5 5 0 1 0 -10 0"></path>
                                    <path d="M12 14v7"></path>
                                    <path d="M9 18h6"></path>
                                  </svg>
                                )}
                                {v.gender === 'male' && (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#4dabf7"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{ marginRight: 4 }}
                                  >
                                    <path d="M10 14m-5 0a5 5 0 1 0 10 0a5 5 0 1 0 -10 0"></path>
                                    <path d="M19 5l-5.4 5.4"></path>
                                    <path d="M19 5h-5"></path>
                                    <path d="M19 5v5"></path>
                                  </svg>
                                )}
                                <span>{v.label}</span>
                              </span>
                              <CoreButton
                                color="secondary"
                                size="small"
                                variant="text"
                                aria-label={
                                  playingVoice === v.value
                                    ? `Pause ${v.label} voice`
                                    : `Play ${v.label} voice`
                                }
                                style={{
                                  minWidth: 0,
                                  padding: 0,
                                  marginLeft: 8,
                                  width: 24,
                                  height: 24,
                                  minHeight: 0,
                                  border: 'none',
                                  background: 'none',
                                }}
                                onClick={e => {
                                  e.stopPropagation()
                                  handlePlayVoice(v.value)
                                }}
                              >
                                <Icon
                                  icon={
                                    playingVoice === v.value
                                      ? 'ion:pause'
                                      : 'iconoir:play-solid'
                                  }
                                  width={24}
                                  height={24}
                                  style={{
                                    marginLeft: 8,
                                    cursor: 'pointer',
                                  }}
                                  aria-label={
                                    playingVoice === v.value
                                      ? `Pause ${v.label} voice`
                                      : `Play ${v.label} voice`
                                  }
                                  onClick={e => {
                                    e.stopPropagation()
                                    handlePlayVoice(v.value)
                                  }}
                                />
                              </CoreButton>
                            </MenuItem>
                          ))}
                        </CoreTextField>
                      </Box>
                    )}
                  <CoreButton
                    className={classes.playButton}
                    color="secondary"
                    size="small"
                    variant="outlined"
                    aria-label="Play advanced reader demo"
                    onClick={() => handlePlayVoice(selectedVoice)}
                  >
                    <Icon
                      icon={
                        playingVoice === selectedVoice
                          ? 'ion:pause'
                          : 'iconoir:play-solid'
                      }
                      className={classes.playIcon}
                    />{' '}
                    <Typography>
                      {playingVoice === selectedVoice ? 'Pause' : 'Play'}
                    </Typography>
                  </CoreButton>
                </div>
              </RadioGroup>
            </Box>
          )}
        </div>
        <Box m={1}></Box>
        {loading ? (
          <div className={classes.bottomButton}>
            <CircularProgress></CircularProgress>
          </div>
        ) : (
          <div className={classes.bottomButton}>
            <CoreButton
              fullWidth={true}
              color={isUserPremium ? 'success' : 'success'}
              size="small"
              variant="contained"
              onClick={saveSettings}
              disabled={isSlugEmpty}
              {...(!isUserPremium && {
                to: undefined,
                component: undefined,
                startIcon: undefined,
              })}
            >
              {isUserPremium
                ? translator._('assignment.courseSettings.save')
                : 'Save'}
            </CoreButton>
          </div>
        )}
      </div>

      <UpgradePopover
        id={id}
        anchorEl={anchorEl}
        handleClose={handleClose}
        open={open}
        header={popupContent.header}
        content={popupContent.content}
      />
    </>
  )
}

export default withStyles(styles)(AdvancedSettings)
